import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/services/db";
import {
  deleteBook,
  importBookFile,
  renameBook,
} from "@/services/bookStorageService";
import type { PdfImportProgress } from "@/services/pdfImportService";
import {
  autoSyncCloudLibrary,
  checkCloudLibrary,
  fetchCloudManifest,
  isTestBookLike,
  readCachedCloudManifest,
  readTombstones,
  sha256Hex,
  uploadCloudBook,
  type CloudBook,
} from "@/services/cloudLibraryService";
import { STARTER_FILE_NAMES } from "@/services/starterLibraryService";
import { PUBLIC_MODE } from "@/services/remoteConfig";
import { reportStorageState } from "@/services/storageDiag";
import { BookCard } from "./BookCard";
import { CloudPlaceholderCard } from "./CloudPlaceholderCard";
import { PwaBanners } from "./PwaBanners";
// CloudLibrary panel deliberately not imported here — auto-sync runs in the
// background and the component is no longer rendered in the main library.
// It's still in the codebase for a future Settings tab.

export function LibraryView() {
  const rawBooks = useLiveQuery(
    // 2026-10-07, Thomas: "By last read". Opening the app should put the
    // book he was reading first — which is what he reaches for — not the
    // most recently imported. Falls back to importedAt for books he has
    // never opened, so a fresh import is still near the top.
    async () => {
      const all = await db.books.toArray();
      return all.sort((a, b) =>
        (b.lastOpenedAt || b.importedAt || "").localeCompare(
          a.lastOpenedAt || a.importedAt || "",
        ),
      );
    },
    []
  );
  // Hide test-harness imports from the local library too — same predicate
  // we use to filter the cloud manifest. Catches anything left behind by
  // headless smoke runs ("PageVoice Test Book", author "Automated Test",
  // etc.) without touching the user's real books.
  const books = useMemo(
    () => (rawBooks ?? []).filter((b) => !isTestBookLike(b.title, b.author)),
    [rawBooks]
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // PDF-conversion progress, only shown while a PDF is being processed.
  const [pdfProgress, setPdfProgress] = useState<PdfImportProgress | null>(null);
  // Background-sync status: ticks while cloud books are being pulled.
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Cloud manifest, used to render placeholder cards for books the user has
  // on the cloud library but hasn't pulled into this device yet. Seeded
  // from localStorage so the grid paints instantly on every launch — that
  // way the home-screen PWA looks identical to the Safari tab the moment
  // it opens, instead of looking empty while auto-sync runs.
  const [cloudManifest, setCloudManifest] = useState<CloudBook[]>(() =>
    readCachedCloudManifest()
  );

  // Refresh-from-cloud: re-fetch the manifest, then silently pull anything
  // not already local. Called once on mount and again whenever the user
  // pull-to-refreshes the library. Returns a count of new books for the
  // brief confirmation toast.
  const refreshFromCloud = useCallback(
    async (cancelledRef?: { cancelled: boolean }): Promise<number> => {
      const isCancelled = () => cancelledRef?.cancelled === true;
      const h = await checkCloudLibrary();
      if (isCancelled() || !h?.ok) return 0;
      try {
        const fresh = await fetchCloudManifest();
        if (!isCancelled()) setCloudManifest(fresh);
      } catch {
        /* keep the cached manifest we already painted */
      }
      try {
        const n = await autoSyncCloudLibrary({
          onProgress: (cur, total, title) => {
            if (!isCancelled()) {
              setSyncMessage(`Syncing ${cur + 1}/${total} — ${title}`);
            }
          },
          // 2026-10-07: storage failures are SHOWN now. They were being
          // swallowed by a bare catch, which is why a device that could not
          // retain a single book looked merely slow for a whole day.
          onError: (msg) => {
            if (!isCancelled()) setError(msg);
          },
        });
        if (!isCancelled()) {
          // After sync: always show the server-side book count so the user
          // can verify at a glance that their library is saved off-device.
          // Earlier behavior was to clear the line when nothing new came
          // down, which made the auto-save invisible.
          try {
            const fresh = await fetchCloudManifest();
            if (!isCancelled()) {
              const total = fresh.filter((b) => !isTestBookLike(b.title, b.author)).length;
              setSyncMessage(
                n > 0
                  ? `Synced ${n} new book${n === 1 ? "" : "s"} from cloud · ${total} saved on server`
                  : `${total} book${total === 1 ? "" : "s"} saved on server`,
              );
            }
          } catch {
            if (!isCancelled()) {
              setSyncMessage(
                n > 0 ? `Synced ${n} new book${n === 1 ? "" : "s"} from cloud.` : null,
              );
            }
          }
        }
        return n;
      } catch {
        return 0;
      }
    },
    []
  );

  // On first mount: refresh the manifest from the network, then silently
  // pull anything not already local. The user never sees a button for this.
  useEffect(() => {
    // Persistence + quota, before any sync runs. Without persistence
    // Android evicts large origin data under pressure and a 55 MB epub is
    // first in the queue. This both REPORTS and REQUESTS it.
    void reportStorageState("libraryMount");
    // ── SHELF STATE FROM HIS DEVICE (2026-10-07) ──────────────────────
    // My duplication theory was DISPROVED locally: importing 2 books took
    // the shelf from 19 cloud tiles to 17 cloud + 2 real, total unchanged,
    // with cloudSha256 set on both. Matching works here.
    //
    // So the cause is something about HIS library that I cannot see, and
    // guessing again would waste another cycle. This reports the shape of
    // the shelf as it actually is on his device: how many local books,
    // how many carry a cloud sha, how many have a stored cover, and how
    // many tiles end up rendering. One launch answers it.
    void (async () => {
      try {
        const all = await db.books.toArray();
        const blobs = await db.bookBlobs.toArray();
        const { storeDiag } = await import("@/services/storageDiag");
        storeDiag("shelf", {
          books: String(all.length),
          withSha: String(all.filter((b) => b.cloudSha256).length),
          blobs: String(blobs.length),
          withCover: String(
            blobs.filter((x) => x.coverBlob && x.coverBlob.size > 0).length,
          ),
          withBlob: String(blobs.filter((x) => x.blob && x.blob.size > 0).length),
        });
      } catch {
        /* diagnostics never break the shelf */
      }
    })();
    const tag = { cancelled: false };
    void refreshFromCloud(tag);
    return () => {
      tag.cancelled = true;
    };
  }, [refreshFromCloud]);

  // Backfill: any local book that isn't on the cloud (either because it
  // pre-dates the auto-mirror feature, or because a prior upload failed
  // silently, or because the user added the book via Safari while the
  // cloud was unreachable) gets pushed up here on Library mount. This
  // is what makes iOS PWA mode and Safari mode see the same library —
  // each device's local-only books reach the server on the next launch,
  // and the autoSyncCloudLibrary on the OTHER device pulls them down.
  // User report 2026-06-28: "books on app version of page view reader do not
  // match browser version. they should match. auto upload books to
  // server. automatic sync."
  useEffect(() => {
    if (!books || books.length === 0) return;
    const tag = { cancelled: false };
    void (async () => {
      try {
        const h = await checkCloudLibrary();
        if (!h?.ok || tag.cancelled) return;
        const remote = await fetchCloudManifest().catch(() => [] as CloudBook[]);
        if (tag.cancelled) return;
        const remoteSet = new Set<string>();
        for (const cb of remote) {
          if (cb.sha256) remoteSet.add(cb.sha256);
          if (cb.filename) remoteSet.add(cb.filename);
        }
        let backfilled = 0;
        for (const b of books) {
          if (tag.cancelled) return;
          // Never push bundled starter-set files into the personal cloud
          // shelf (2026-07-10): a wrongly-seeded starter book would
          // otherwise be backfilled before the cleanup migration removes
          // it. Genuine copies of these classics live on the cloud under
          // their own filenames/SHAs and are unaffected.
          if (b.fileName && STARTER_FILE_NAMES.has(b.fileName)) continue;
          // Skip if we know it's already on the cloud.
          if (b.cloudSha256 && remoteSet.has(b.cloudSha256)) continue;
          if (b.fileName && remoteSet.has(b.fileName)) continue;
          // Pull the blob from local IDB, upload it.
          const blob = await db.bookBlobs.get(b.id).then(r => r?.blob);
          if (!blob) continue;
          try {
            const sha = await sha256Hex(blob);
            if (remoteSet.has(sha)) continue;
            const file = new File(
              [blob],
              b.fileName || `${b.title || "book"}.epub`,
              { type: "application/epub+zip" },
            );
            await uploadCloudBook(file);
            // Persist the SHA so subsequent boots skip the re-upload check.
            await db.books.put({ ...b, cloudSha256: sha });
            backfilled += 1;
          } catch {
            /* silent — try again next launch */
          }
        }
        if (backfilled > 0 && !tag.cancelled) {
          setSyncMessage(`Backed up ${backfilled} local book${backfilled === 1 ? "" : "s"} to the server.`);
          setTimeout(() => !tag.cancelled && setSyncMessage(null), 4000);
        }
      } catch {
        /* silent */
      }
    })();
    return () => {
      tag.cancelled = true;
    };
    // Don't re-fire on every books change — only when the count changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [books?.length]);

  // Repair stored rows once the manifest is in hand. Keyed on the manifest
  // because at mount it is still empty — an earlier draft of this ran inside
  // the mount effect and could never have fired.
  useEffect(() => {
    // ── BACKFILL cloudSha256 ON WHAT IS ALREADY STORED ────────────────
    // The display guard above stops the duplicate tiles immediately, but
    // the underlying mismatch would persist. Repair the stored rows so the
    // strict sha match works from here on — matched on title+author
    // against the manifest, write-only, and it never re-downloads anything.
    void (async () => {
      try {
        if (!cloudManifest.length) return;
        const norm = (v?: string) =>
          (v ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 60);
        const byMeta = new Map<string, string>();
        for (const cb of cloudManifest) {
          byMeta.set(norm(cb.title) + "|" + norm(cb.author), cb.sha256);
        }
        const locals = await db.books.toArray();
        let fixed = 0;
        for (const b of locals) {
          if (b.cloudSha256) continue;
          const sha = byMeta.get(norm(b.title) + "|" + norm(b.author));
          if (!sha) continue;
          await db.books.put({ ...b, cloudSha256: sha });
          fixed += 1;
        }
        if (fixed > 0) {
          const { storeDiag } = await import("@/services/storageDiag");
          storeDiag("shaBackfill", { fixed: String(fixed), of: String(locals.length) });
        }
      } catch {
        /* repair is best-effort; the display guard already covers the UI */
      }
    })();

  }, [cloudManifest]);

  // Build the cloud-only set: manifest minus anything already imported
  // locally and minus anything the user explicitly deleted (tombstones).
  // Re-derives whenever local books or the manifest change.
  const cloudOnly = useMemo<CloudBook[]>(() => {
    if (!cloudManifest.length) return [];
    // ── EVERY BOOK TWICE (2026-10-07) ──────────────────────────────────
    // Thomas, from the device: "1914 loading twice cover. Big mess."
    //
    // A manifest entry only counted as "already local" if it matched a
    // book's cloudSha256 or its exact fileName. Any book whose stored sha
    // is absent or differs from the manifest's — and whose filename was
    // rewritten on import — therefore matched NEITHER, so it rendered as a
    // real BookCard AND again as a cloud placeholder. Nineteen books, 38
    // tiles, half of them captioned and coverless.
    //
    // Matching is now layered, strictest first, so a correct match still
    // wins and the looser rules only catch what the strict ones miss:
    //   1. cloudSha256          — exact, the intended key
    //   2. fileName             — exact, as before
    //   3. title + author       — normalised; the same book re-imported
    //                             keeps its metadata even when the file
    //                             was renamed or re-wrapped
    // (3) is a display-time guard only; it never writes or deletes.
    const tombs = readTombstones();
    const known = new Set<string>();
    const norm = (v?: string) =>
      (v ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 60);
    const byMeta = new Set<string>();
    for (const b of books ?? []) {
      if (b.cloudSha256) known.add(b.cloudSha256);
      if (b.fileName) known.add(b.fileName);
      const k = norm(b.title) + "|" + norm(b.author);
      if (k.length > 2) byMeta.add(k);
    }
    return cloudManifest.filter(
      (cb) =>
        !known.has(cb.sha256) &&
        !known.has(cb.filename) &&
        !byMeta.has(norm(cb.title) + "|" + norm(cb.author)) &&
        !tombs.has(cb.sha256),
    );
  }, [books, cloudManifest]);

  const onFile = useCallback(async (file: File) => {
    setBusy(true);
    setError(null);
    setPdfProgress(null);
    // The Import button click is the strongest user gesture we'll see —
    // Safari is most likely to grant persistent storage here. Without
    // this, iOS will auto-evict the imported book under storage pressure
    // or simple non-use, which is the 'shortcut forgets the library'
    // symptom that keeps recurring on iOS.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const storage = (navigator as any).storage;
      if (storage?.persist) {
        const already = await storage.persisted?.();
        if (!already) await storage.persist();
      }
    } catch {
      /* persistence is best-effort */
    }
    try {
      // Router function handles both EPUB and PDF. PDFs get converted to
      // EPUB on-device before being stored, so the rest of the reader
      // stays a pure EPUB pipeline.
      await importBookFile(file, (p) => setPdfProgress(p));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setPdfProgress(null);
    }
  }, []);

  const onPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      Array.from(files).forEach((f) => onFile(f));
      e.target.value = "";
    },
    [onFile]
  );

  // Drag & drop (disabled in the public demo build alongside Import)
  const [hover, setHover] = useState(false);
  useEffect(() => {
    if (PUBLIC_MODE) return;
    const onDragOver = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
        setHover(true);
      }
    };
    const onDragLeave = () => setHover(false);
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setHover(false);
      const files = e.dataTransfer?.files;
      if (!files) return;
      Array.from(files).forEach((f) => onFile(f));
    };
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [onFile]);

  // Pull-to-refresh state. The scroll container is the outer div with
  // overflow-y-auto. We track the touch finger from the moment it lands
  // while scrollTop === 0, expose a translateY offset on the spinner row,
  // and fire refreshFromCloud once the user drags past the threshold and
  // releases. Subtle damping makes the rubber-band feel right.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const [pullPx, setPullPx] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const PULL_THRESHOLD = 70;
  const PULL_MAX = 110;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop > 0) {
      touchStartYRef.current = null;
      return;
    }
    if (refreshing) return;
    touchStartYRef.current = e.touches[0]?.clientY ?? null;
  }, [refreshing]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const startY = touchStartYRef.current;
    if (startY == null) return;
    const y = e.touches[0]?.clientY ?? startY;
    const delta = y - startY;
    if (delta <= 0) {
      setPullPx(0);
      return;
    }
    // Rubber-band: half the actual drag past 40px feels right on iOS.
    const damped = delta <= 40 ? delta : 40 + (delta - 40) * 0.4;
    const clamped = Math.min(PULL_MAX, damped);
    setPullPx(clamped);
  }, []);

  const handleTouchEnd = useCallback(async () => {
    const px = pullPx;
    touchStartYRef.current = null;
    if (px >= PULL_THRESHOLD && !refreshing) {
      setRefreshing(true);
      setPullPx(PULL_THRESHOLD); // hold open while spinning
      try {
        await refreshFromCloud();
      } finally {
        setRefreshing(false);
        setPullPx(0);
      }
    } else {
      setPullPx(0);
    }
  }, [pullPx, refreshing, refreshFromCloud]);

  const onDelete = useCallback(async (id: string) => {
    if (!window.confirm("Delete this book and its progress?")) return;
    await deleteBook(id);
  }, []);

  const onRename = useCallback(async (id: string) => {
    const book = await db.books.get(id);
    if (!book) return;
    const title = window.prompt("Title", book.title) ?? book.title;
    const author = window.prompt("Author", book.author ?? "") ?? book.author;
    await renameBook(id, { title, author });
  }, []);

  // Pull-to-refresh indicator. Rotates the arrow toward the threshold
  // and swaps to a spinning ring once a refresh is in flight.
  const pullProgress = Math.min(1, pullPx / PULL_THRESHOLD);
  const PullIndicator = (
    <div
      aria-hidden
      style={{
        height: `${pullPx}px`,
        transition: refreshing || pullPx === 0 ? "height 180ms ease" : "none",
      }}
      className="pv-fg-soft flex items-end justify-center overflow-hidden text-xs"
    >
      {pullPx > 6 && (
        <div className="mb-2 flex items-center gap-2">
          <span
            className="inline-block"
            style={{
              transform: refreshing
                ? "rotate(0deg)"
                : `rotate(${pullProgress >= 1 ? 180 : pullProgress * 180}deg)`,
              transition: refreshing ? "none" : "transform 120ms ease",
              animation: refreshing ? "pv-spin 0.9s linear infinite" : undefined,
              display: "inline-block",
            }}
          >
            {refreshing ? "⟳" : "↓"}
          </span>
          <span>
            {refreshing
              ? "Refreshing library…"
              : pullProgress >= 1
              ? "Release to refresh"
              : "Pull to refresh"}
          </span>
        </div>
      )}
      <style>{`@keyframes pv-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );

  return (
    <div
      ref={scrollRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      className="pv-fg relative flex h-full flex-col overflow-y-auto"
    >
      {PullIndicator}
      <PwaBanners />
      <header
        className="pv-border flex items-center justify-between border-b px-5"
        style={{
          // In standalone mode on iOS the status-bar / Dynamic Island sits
          // OVER the page (black-translucent style), which was hiding the
          // Import button at the top. Pad the header by the safe-area
          // inset so the header content drops below the system bar.
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "4px",
          paddingLeft: "calc(env(safe-area-inset-left, 0px) + 20px)",
          paddingRight: "calc(env(safe-area-inset-right, 0px) + 20px)",
        }}
      >
        <div className="flex flex-col items-center leading-tight tracking-tight">
          {/* Homepage masthead. Title text-2xl bold + " Reader" regular.
              Version stamp centered under the title — italic serif, no
              "v" prefix, same foreground color as the title so both
              read as one masthead block. */}
          <div className="pv-fg text-base">
            <span className="font-bold">PageVoice</span>
            <span className="font-normal"> Reader</span>
          </div>
          <div
            className="pv-fg-soft text-[11px] italic"
            style={{
              fontFamily: '"Iowan Old Style", Georgia, serif',
              letterSpacing: "0.02em",
            }}
          >
            {__PV_BUILD_VERSION__}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Classics — the in-app store for free public-domain books. On
              the public build (where file Import is disabled) this is the
              main way to get books; on the full build it sits next to
              Import. Accent-filled in public mode since it's the primary
              action there; outlined next to Import otherwise. */}
          <Link
            to="/classics"
            className={
              PUBLIC_MODE
                ? "pv-accent-bg rounded-md px-5 py-2.5 text-base font-semibold tracking-tight shadow-sm active:scale-95 hover:opacity-90"
                : "pv-border pv-fg rounded-md border px-4 py-2.5 text-base font-semibold tracking-tight shadow-sm active:scale-95 hover:opacity-80"
            }
          >
            Classics
          </Link>
          {/* Import is a device-local feature; the public demo deployment
              (VITE_PUBLIC_MODE=1) gets books via Classics instead. */}
          {!PUBLIC_MODE && (
            <label className="pv-accent-bg cursor-pointer rounded-md px-5 py-2.5 text-base font-semibold tracking-tight shadow-sm active:scale-95 hover:opacity-90">
              Import
              <input
                type="file"
                // Accept both EPUB and PDF in a single picker. The importer
                // checks the file extension + MIME and routes the file to the
                // right pipeline (PDFs are converted to EPUB on-device).
                accept=".epub,.pdf,application/epub+zip,application/pdf"
                multiple
                className="hidden"
                onChange={onPick}
              />
            </label>
          )}
        </div>
      </header>

      {/* Hero tagline — visible above the shelf on every visit. Puts the
          reader's identity on the same panel as the books: translation
          first, then voices, then highlights, then privacy. */}
      {/* 2026-10-07 — THE "EVERY BOOK AD" IS GONE.
          Thomas asked three times: "Remove every language thing ad for
          now", then twice "remove old ad". It was the marketing hero —
          "Every book. Every language. On your device." plus the
          Translation / Voices / Highlights / Privacy list — and at 578x768
          it sat above a shelf whose entire job is showing books.
          It was onboarding copy, and EmptyState below already covers the
          genuinely-empty-shelf case with better guidance, so nothing
          needed to replace it. */}

      {error && (
        <div className="mx-5 mt-4 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300 dark:text-red-300">
          {error}
        </div>
      )}
      {busy && !pdfProgress && (
        <div className="pv-fg-soft mx-5 mt-4 text-sm">Importing…</div>
      )}
      {pdfProgress && (
        <div className="pv-border mx-5 mt-4 rounded border px-3 py-2 text-sm">
          <div className="pv-fg font-medium">
            {pdfProgress.phase === "loading"     && "Opening PDF…"}
            {pdfProgress.phase === "extracting"  && (
              <>Reading page <span className="tabular-nums">{pdfProgress.page ?? 0}</span> of <span className="tabular-nums">{pdfProgress.pages ?? "?"}</span></>
            )}
            {pdfProgress.phase === "assembling"  && "Building EPUB…"}
            {pdfProgress.phase === "done"        && "Done."}
          </div>
          {pdfProgress.pages && pdfProgress.phase === "extracting" && (
            <div className="pv-border-soft mt-2 h-1.5 w-full overflow-hidden rounded bg-black/20">
              <div
                className="pv-accent-bg h-full transition-all"
                style={{
                  width: `${Math.min(100, ((pdfProgress.page ?? 0) / pdfProgress.pages) * 100)}%`,
                }}
              />
            </div>
          )}
          {pdfProgress.message && (
            <div className="pv-fg-soft mt-1 text-xs">{pdfProgress.message}</div>
          )}
        </div>
      )}
      {syncMessage && !pdfProgress && !busy && (
        <div className="pv-fg-soft mx-5 mt-4 text-xs italic">
          {syncMessage}
        </div>
      )}

      {/* Unified grid: local books + cloud-only placeholders rendered
          together. On the web tab and the home-screen PWA both, the user
          sees the same library at the same instant — cloud-only entries
          fade in from the cached manifest with a ☁ badge, and tap-to-read
          downloads the book just-in-time. Auto-sync continues to backfill
          everything in the background, so every device converges to the
          same fully-local state without the user managing it. */}
      {(books && books.length === 0) && cloudOnly.length === 0 ? (
        <EmptyState />
      ) : (
        // 2026-10-07, Thomas: "First covers only. More books." Density comes
        // from smaller cells and no caption, NOT from removing scrolling —
        // scrolling stays until paginated navigation is proven on his device.
        // Cells are ~100px so a 578px-wide BOOX fits 5 across instead of 2.
        <div className="grid grid-cols-5 gap-2 p-3 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-8 xl:grid-cols-10">
          {books?.map((b) => (
            <BookCard
              key={b.id}
              book={b}
              onDelete={onDelete}
              onRename={onRename}
              dense
            />
          ))}
          {cloudOnly.map((cb) => (
            <CloudPlaceholderCard key={`cloud-${cb.id}`} cloud={cb} dense />
          ))}
        </div>
      )}

      {/* CloudLibrary panel removed 2026-06-19 — auto-sync now handles
          incoming books, autoMirrorBookToCloud handles outgoing. The
          component is kept in the codebase for a future Settings tab
          where the user can manage cloud entries directly. */}

      {hover && (
        <div className="pointer-events-none fixed inset-0 flex items-center justify-center bg-black/50">
          <div className="rounded-lg border-2 border-dashed border-white/70 bg-black/60 px-6 py-4 text-white">
            Drop EPUB or PDF to import
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  // The empty state is the same on every install now that the cloud library
  // auto-syncs in the background. No PWA-mode-specific copy needed.
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6 py-10 text-center">
      <div className="pv-fg text-2xl font-serif">Your shelf is empty.</div>
      <div className="pv-fg-soft max-w-md text-sm">
        Browse the free <Link to="/classics" className="underline">Classics</Link> catalog
        to add public-domain books with one tap
        {PUBLIC_MODE
          ? "."
          : ", or drop an EPUB or PDF onto this window. PDFs convert to EPUB on-device, and whatever you import syncs to your other devices automatically."}
      </div>
    </div>
  );
}
